import { Fragment, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { mesAno, textoLivre } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { Barra, BotaoExportar, LinhaEscopo, Rolagem, Segmentado, TD, TH, useParametro, usePeriodo, Valor } from './comum';
import { Detalhe } from './Detalhe';
import { BASES, INTERCOMPANY, type Base, type Dre as DreApi, type Intercompany, type LinhaDre, type NoArvore } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';

const DESTAQUE: Record<string, string> = { subtotal: 'font-semibold bg-sup-2', resultado: 'font-semibold bg-acento-suave', balde: 'text-tinta-2', fora: 'text-tinta-2', grupo: '' };

/** Seletor de "entre empresas do grupo" (separados ou eliminados) e "usar como padrão" para quem configura. */
export function SeletorIntercompany({ valor, aoMudar }: { valor: Intercompany; aoMudar: (v: Intercompany) => void }) {
  const { t } = useTranslation('relatorios');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/relatorios/configuracao', { intercompany_padrao: valor }),
    onSuccess: () => {
      toast.sucesso(t('comum.padraoSalvo'));
      void qc.invalidateQueries({ queryKey: ['/relatorios/configuracao'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <>
      <Segmentado
        rotulo={t('comum.intercompany')}
        valor={valor}
        aoMudar={aoMudar}
        opcoes={[
          { valor: 'eliminados', rotulo: t('comum.eliminados') },
          { valor: 'separados', rotulo: t('comum.separados') },
        ]}
      />
      {podeNoWorkspace(s, 'relatorios.configurar') && (
        <Botao variante="fantasma" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
          {t('comum.usarPadrao')}
        </Botao>
      )}
    </>
  );
}

/** Intercompany vigente: o da URL ou o padrão da configuração. */
export function useIntercompany(): [Intercompany | undefined, (v: Intercompany) => void, Intercompany] {
  const cfg = useQuery({ queryKey: ['/relatorios/configuracao'], queryFn: () => get<{ intercompany_padrao: Intercompany }>('/relatorios/configuracao') });
  const padrao = cfg.data?.intercompany_padrao ?? 'eliminados';
  const [valor, mudar] = useParametro<Intercompany | ''>('intercompany', '', INTERCOMPANY);
  return [valor || undefined, (v) => mudar(v), (valor || padrao) as Intercompany];
}

function NosArvore({ nos, nivel, moeda, aoAbrir }: { nos: NoArvore[]; nivel: number; moeda: string; aoAbrir: (n: NoArvore) => void }) {
  const { t } = useTranslation('relatorios');
  return (
    <>
      {nos.map((n) => {
        const nome = n.categoria_id ? `${n.codigo ? `${n.codigo} ` : ''}${rotuloCategoria(n.nome)}` : t('comum.semCategoria');
        return (
          <Fragment key={n.categoria_id ?? 'sem'}>
            <tr className="border-b border-borda last:border-0">
              <td className={TD} style={{ paddingLeft: `${1 + nivel * 1.25}rem` }}>
                <button
                  type="button"
                  onClick={() => aoAbrir(n)}
                  aria-label={t('comum.detalhar', { nome })}
                  className={`text-left break-words text-tinta-1 underline-offset-2 hover:text-acento-forte hover:underline${n.sintetica ? ' font-medium' : ''}`}
                >
                  {nome}
                </button>
              </td>
              <td className={`${TD} text-right`}>
                <Valor centavos={n.total_centavos} moeda={moeda} />
              </td>
            </tr>
            {n.filhos.length > 0 && <NosArvore nos={n.filhos} nivel={nivel + 1} moeda={moeda} aoAbrir={aoAbrir} />}
          </Fragment>
        );
      })}
    </>
  );
}

/** R2 · DRE: competência (padrão) ou caixa; retrato em cascata ou árvore; série por mês; consolidado com eliminações. */
export function Dre() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [base, setBase] = useParametro<Base>('base', 'competencia', BASES);
  const [visao, setVisao] = useParametro<'retrato' | 'arvore'>('visao', 'retrato', ['retrato', 'arvore']);
  const [serie, setSerie] = useParametro<'0' | '1'>('serie', '0', ['0', '1']);
  const [inter, setInter, interVigente] = useIntercompany();
  const [aberto, setAberto] = useState<{ titulo: string; params: Record<string, unknown> } | null>(null);
  const params = { de: periodo.de, ate: periodo.ate, base, serie: serie === '1' ? 'true' : undefined, intercompany: inter };
  const q = useQuery({ queryKey: ['/relatorios/dre', params], queryFn: () => get<DreApi>('/relatorios/dre', params), placeholderData: keepPreviousData });
  const abrir = (titulo: string, extra: Record<string, unknown>) => setAberto({ titulo, params: { de: periodo.de, ate: periodo.ate, base, intercompany: inter, ...extra } });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <Segmentado
          rotulo={t('comum.baseRotulo')}
          valor={base}
          aoMudar={setBase}
          opcoes={[
            { valor: 'competencia', rotulo: t('comum.base.competencia') },
            { valor: 'caixa', rotulo: t('comum.base.caixa') },
          ]}
        />
        <Segmentado
          rotulo={t('dre.visao')}
          valor={visao}
          aoMudar={setVisao}
          opcoes={[
            { valor: 'retrato', rotulo: t('dre.retrato') },
            { valor: 'arvore', rotulo: t('dre.arvore') },
          ]}
        />
        {visao === 'retrato' && (
          <Segmentado
            rotulo={t('dre.serie')}
            valor={serie}
            aoMudar={setSerie}
            opcoes={[
              { valor: '0', rotulo: t('dre.periodoInteiro') },
              { valor: '1', rotulo: t('dre.serie') },
            ]}
          />
        )}
        {q.data?.escopo.modo === 'consolidado' && <SeletorIntercompany valor={interVigente} aoMudar={setInter} />}
        <BotaoExportar relatorio="dre" parametros={{ de: periodo.de, ate: periodo.ate, base, intercompany: inter }} />
      </Barra>

      <Estado consulta={q}>
        {(d) => (
          <>
            <div>
              <h2 className="text-base font-semibold text-tinta-1">{t('dre.titulo', { base: t(`comum.base.${d.base}`).toLowerCase() })}</h2>
              <LinhaEscopo escopo={d.escopo} extra={d.escopo.modo === 'consolidado' && d.parceiros_do_grupo > 0 ? <span>{`. ${t('comum.grupoParceiros', { count: d.parceiros_do_grupo })}`}</span> : null} />
            </div>
            {d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('dre.vazioTitulo')} texto={t('dre.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => {
                const consolidado = d.escopo.modo === 'consolidado';
                const comEliminacoes = consolidado && d.intercompany === 'eliminados';
                const empresas = consolidado ? d.escopo.empresas.filter((e) => m.linhas.some((l) => l.por_empresa?.[e.id] !== undefined)) : [];
                return (
                  <Cartao key={m.moeda} titulo={d.por_moeda.length > 1 ? t('comum.moeda', { moeda: m.moeda }) : undefined} semPadding>
                    <Rolagem rotulo={`${t('comum.tabela')} (${m.moeda})`}>
                      {visao === 'retrato' ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('dre.linha')}</th>
                              {d.meses?.map((mes) => (
                                <th key={mes} scope="col" className={`${TH} text-right`}>{mesAno(`${mes}-01`)}</th>
                              ))}
                              {empresas.map((e) => (
                                <th key={e.id} scope="col" className={`${TH} text-right`}>{e.nome}</th>
                              ))}
                              {comEliminacoes && <th scope="col" className={`${TH} text-right`}>{t('dre.eliminacoes')}</th>}
                              <th scope="col" className={`${TH} text-right`}>{consolidado ? t('dre.consolidado') : t('comum.total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.linhas.map((l) => {
                              const nome = t(`dre.linhas.${l.chave as LinhaDre}`);
                              const oculta = (l.chave === 'sem_grupo' || l.chave === 'sem_categoria' || l.chave === 'fora_dre') && l.valor_centavos === 0;
                              if (oculta) return null;
                              return (
                                <tr key={l.chave} className={`border-b border-borda last:border-0 ${DESTAQUE[l.tipo] ?? ''} ${l.tipo === 'fora' ? 'border-t-2' : ''}`}>
                                  <th scope="row" className={`${TD} text-left font-normal ${l.tipo === 'subtotal' || l.tipo === 'resultado' ? 'font-semibold' : ''}`}>
                                    <button type="button" onClick={() => abrir(nome, { linha: l.chave })} aria-label={t('comum.detalhar', { nome })} className="text-left break-words underline-offset-2 hover:text-acento-forte hover:underline">
                                      {nome}
                                    </button>
                                  </th>
                                  {d.meses?.map((mes, i) => (
                                    <td key={mes} className={`${TD} text-right`}>
                                      <Valor centavos={l.serie?.[i] ?? 0} moeda={m.moeda} />
                                    </td>
                                  ))}
                                  {empresas.map((e) => (
                                    <td key={e.id} className={`${TD} text-right`}>
                                      <Valor centavos={l.por_empresa?.[e.id] ?? 0} moeda={m.moeda} />
                                    </td>
                                  ))}
                                  {comEliminacoes && (
                                    <td className={`${TD} text-right text-tinta-2`}>
                                      <Valor centavos={-(l.eliminacoes_centavos ?? 0)} moeda={m.moeda} />
                                    </td>
                                  )}
                                  <td className={`${TD} text-right`}>
                                    <Valor centavos={l.valor_centavos} moeda={m.moeda} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('dre.linha')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('comum.total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...m.arvore.filter((a) => a.balde !== 'fora_dre'), ...m.arvore.filter((a) => a.balde === 'fora_dre')].map((a) => (
                              <Fragment key={a.balde}>
                                {a.balde === 'fora_dre' && (
                                  <tr className="border-b border-borda bg-acento-suave font-semibold">
                                    <th scope="row" className={`${TD} text-left`}>{t('dre.linhas.resultado')}</th>
                                    <td className={`${TD} text-right`}>
                                      <Valor centavos={m.resultado_centavos} moeda={m.moeda} />
                                    </td>
                                  </tr>
                                )}
                                <tr className="border-b border-borda bg-sup-2 font-semibold">
                                  <th scope="row" className={`${TD} text-left`}>{t(`dre.linhas.${a.balde as LinhaDre}`)}</th>
                                  <td className={`${TD} text-right`}>
                                    <Valor centavos={a.total_centavos} moeda={m.moeda} />
                                  </td>
                                </tr>
                                <NosArvore nos={a.nos} nivel={1} moeda={m.moeda} aoAbrir={(n) => abrir(rotuloCategoria(n.nome) || t('comum.semCategoria'), n.categoria_id ? { categoria_id: n.categoria_id } : { linha: 'sem_categoria' })} />
                              </Fragment>
                            ))}
                            {!m.arvore.some((a) => a.balde === 'fora_dre') && (
                              <tr className="bg-acento-suave font-semibold">
                                <th scope="row" className={`${TD} text-left`}>{t('dre.linhas.resultado')}</th>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={m.resultado_centavos} moeda={m.moeda} />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </Rolagem>
                    <div className="border-t border-borda px-4 py-3 text-xs text-tinta-3">
                      <p>{t('dre.semGrupoAjuda')}</p>
                      <p>{t('dre.foraAjuda')}</p>
                    </div>
                  </Cartao>
                );
              })
            )}
          </>
        )}
      </Estado>
      {aberto && <Detalhe titulo={textoLivre(aberto.titulo)} caminho="/relatorios/dre/detalhe" params={aberto.params} aoFechar={() => setAberto(null)} />}
    </div>
  );
}
